import { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Card,
  Form,
  Row,
  Col,
  Button,
  Stack,
  Alert,
  Container,
} from "react-bootstrap";
import { Audit } from "../../models/Audit";
import { AuditModule } from "../../models/AuditModule";
import { AuditQuestion } from "../../models/AuditQuestion";
import { AuditDetail } from "../../models/AuditDetail";
import { DisplayAuditQuestions } from "../DisplayAuditQuestions";
import FileUpload from "../FillAudit/FileUpload";
import AuditService from "../../services/audit.service";
import { useLoading } from "../App/LoadingContext";
import validation from "../../Helpers/utils/validation";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretLeft,
  faCaretRight,
  faSave,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons";

const FillAudit = () => {
  const [audit, setAudit] = useState<Audit | null>(null);
  const [modules, setModules] = useState<AuditModule[]>([]);
  const [localQuestions, setLocalQuestions] = useState<AuditQuestion[]>([]);
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<number>>(
    new Set()
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isNeededReloadFiles, setIsNeededReloadFiles] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");

  const { wrapWithLoading } = useLoading();
  const navigate = useNavigate();
  const auditId = parseInt(localStorage.getItem("auditId") || "-1");

  useEffect(() => {
    const loadAllData = async () => {
      if (auditId === -1) return;

      const auditData = await AuditService.getQuestionaryById(auditId);
      setAudit(auditData);

      const modulesData = await AuditService.getModules(auditData.policyTypeId);
      const sortedModules = modulesData
        .sort((a, b) => a.position - b.position)
        .map((m) => ({
          ...m,
          sections: (m.sections || [])
            .sort((a, b) => a.position - b.position)
            .map((s) => ({
              ...s,
              subsections:
                s.subsections?.sort((a, b) => a.position - b.position) ?? [],
            })),
        }));
      setModules(sortedModules);

      const allSubsections = sortedModules.flatMap(
        (m) => m.sections?.flatMap((s) => s.subsections ?? []) ?? []
      );
      const questionsPromises = allSubsections.map((sub) =>
        AuditService.getQuestions(sub.id, auditId)
      );

      const questionsBySubsection = await Promise.all(questionsPromises);
      const allQuestionsRaw = questionsBySubsection.flat();

      const details = await AuditService.getQuestionaryDetail(auditId);
      const allQuestionsWithDetails = allQuestionsRaw.map((q) => {
        const questionDetails = details.filter((d) => d.auditQuestionId === q.id);
        if (questionDetails.length === 0) return q;

        const updatedQuestion = { ...q };

        if ([1, 5, 7, 12, 13].includes(q.questionTypeId)) {
          // Tipos de respuesta única con texto
          const detail = questionDetails[0];
          updatedQuestion.answerText = detail.answerText;
        } else if (q.questionTypeId === 2) {
          // Tipo 2: Selección única (radio button)
          const detail = questionDetails[0];
          updatedQuestion.auditAnswers = q.auditAnswers?.map((ans) =>
            ans.id === detail.auditAnswerId
              ? { ...ans, isChecked: true }
              : { ...ans, isChecked: false }
          );
        } else if (q.questionTypeId === 3) {
          // Tipo 3: Selección múltiple (checkbox) - puede tener múltiples details
          const checkedAnswerIds = new Set(questionDetails.map(d => d.auditAnswerId));
          updatedQuestion.auditAnswers = q.auditAnswers?.map((ans) => ({
            ...ans,
            isChecked: checkedAnswerIds.has(ans.id)
          }));
        } else if ([4, 8].includes(q.questionTypeId)) {
          const detail = questionDetails[0];
          updatedQuestion.answerText = detail.answerText;
          updatedQuestion.auditAnswers = q.auditAnswers?.map((ans) => {
            if (ans.id === detail.auditAnswerId) {
              return {
                ...ans,
                answerText: detail.answerText,
              };
            }
            return ans;
          });
        }
        return updatedQuestion;
      });

      setLocalQuestions(allQuestionsWithDetails);
    };

    wrapWithLoading(loadAllData)();
  }, [auditId, isNeededReloadFiles]);

  const handleSetQuestions = (newQuestions: AuditQuestion[]) => {
    setLocalQuestions(newQuestions);
  };

  const handleSaveButton = wrapWithLoading(async () => {
    // Resetea el estado de validación al iniciar el guardado.
    setShowValidationError(false);
    setInvalidQuestionIds(new Set());

    setIsSaving(true);
    try {
      const detailsToSave: AuditDetail[] = [];
      localQuestions.forEach((q) => {
        const questionHasTextAnswer =
          q.answerText &&
          q.answerText.trim() !== "" &&
          q.answerText.trim() !== "[]";
        const questionHasCheckedAnswer = q.auditAnswers?.some(
          (a) => a.isChecked
        );

        if (questionHasTextAnswer) {
          const answer = q.auditAnswers?.[0];
          if (answer) {
            detailsToSave.push({
              id: answer.auditDetailId || 0,
              auditId: auditId,
              auditQuestionId: q.id,
              answerText: q.answerText,
              auditAnswerId: answer.id,
              comment: answer.comment,
            });
          }
        } else if (questionHasCheckedAnswer) {
          q.auditAnswers
            ?.filter((a) => a.isChecked)
            .forEach((answer) => {
              detailsToSave.push({
                id: answer.auditDetailId || 0,
                auditId: auditId,
                auditQuestionId: q.id,
                answerText: undefined,
                auditAnswerId: answer.id,
                comment: answer.comment,
              });
            });
        }
      });
      await AuditService.saveQuestionaryDetail(detailsToSave);
      setShowSavedMessage(true);
    } catch (error) {
      setErrorTitle("Error al Guardar");
      setErrorMessage("No se pudo guardar el progreso de la auditoría.");
      setShowValidationError(true);
    } finally {
      setIsSaving(false);
    }
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const failedIds = await validation.getInvalidAuditQuestionIds(
      localQuestions,
      auditId
    );
    setInvalidQuestionIds(new Set(failedIds));

    if (failedIds.length > 0) {
      setErrorTitle("Error de Validación");
      setErrorMessage(
        "Debe contestar las preguntas requeridas para poder continuar"
      );
      setShowValidationError(true);
      window.scrollTo(0, 0);
      return;
    }

    setShowValidationError(false);
    await handleSaveButton();
    navigate("/Summary", { state: { auditId } });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (
      event.key === "Enter" &&
      (event.target as HTMLElement).tagName.toLowerCase() === "input"
    ) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (showValidationError || showSavedMessage) {
      const timer = setTimeout(() => {
        setShowValidationError(false);
        setShowSavedMessage(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showValidationError, showSavedMessage]);

  return (
    <div>
      <style type="text/css">{`
                .custom-tabs-container .nav-tabs { border-bottom-color: #dee2e6; }
                .custom-tabs-container .nav-tabs .nav-link { font-size: 1.05rem; color: #495057; background-color: #f8f9fa; border-color: #dee2e6 #dee2e6 #dee2e6; margin-right: 2px; border-top-left-radius: 0.375rem; border-top-right-radius: 0.375rem; transition: all 0.2s ease-in-out; }
                .custom-tabs-container .nav-tabs .nav-link:hover:not(.active) { border-color: #0d6efd; color: #0d6efd; }
                .custom-tabs-container .nav-tabs .nav-link.active { color: #fff !important; background-color: #0d6efd; border-color: #0d6efd #0d6efd #fff; font-weight: bold; }
            `}</style>
      <div className="notification-container">
        {showValidationError && (
          <Alert
            variant="danger"
            onClose={() => setShowValidationError(false)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{errorTitle}</Alert.Heading>
            <p>{errorMessage}</p>
          </Alert>
        )}
        {showSavedMessage && (
          <Alert
            variant="success"
            onClose={() => setShowSavedMessage(false)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>Éxito</Alert.Heading>
            <p>La auditoría ha sido guardada.</p>
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown} noValidate>
          <Card>
            <Card.Header className="card-header-main bg-primary text-white">
              <Row>
                <Col>Compañía: {audit?.company?.name}</Col>
                <Col className="text-end">
                  Número de Póliza: {audit?.policyNumber}
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <div className="custom-tabs-container">
                <Tabs
                  activeKey={currentTab}
                  onSelect={(k) => setCurrentTab(k ? parseInt(k) : 0)}
                  id="tabModules"
                  className="mb-3"
                >
                  {modules.map((module, index) => (
                    <Tab eventKey={index} title={module.name} key={module.id}>
                      {module.sections?.map((section) => (
                        <Card key={section.id} className="mb-3">
                          <Card.Header className="card-header-section bg-primary text-white">
                            {section.name}
                          </Card.Header>
                          <Card.Body>
                            {section.subsections?.map((subsection) => (
                              <Card key={subsection.id} className="mb-3">
                                <Card.Header className="card-header-subsection bg-secondary text-white">
                                  {subsection.name}
                                </Card.Header>
                                <Card.Body>
                                  <DisplayAuditQuestions
                                    SubsectionID={subsection.id}
                                    questions={localQuestions}
                                    setQuestions={handleSetQuestions}
                                    invalidIds={invalidQuestionIds}
                                    AuditId={auditId}
                                    onHandleReloadFiles={() =>
                                      setIsNeededReloadFiles((p) => !p)
                                    }
                                    isNeededReloadFiles={isNeededReloadFiles}
                                  />
                                </Card.Body>
                              </Card>
                            ))}
                          </Card.Body>
                        </Card>
                      ))}
                    </Tab>
                  ))}
                  <Tab
                    eventKey={modules.length}
                    title="Archivos"
                    key="files-tab"
                  >
                    <Card>
                      <Card.Body>
                        <FileUpload
                          auditId={auditId}
                          onUploadSuccess={() =>
                            console.log("Upload successful")
                          }
                          isNeededReloadFiles={isNeededReloadFiles}
                          onHandleReloadFiles={() =>
                            setIsNeededReloadFiles((p) => !p)
                          }
                        />
                      </Card.Body>
                    </Card>
                  </Tab>
                </Tabs>
              </div>
              <div style={{ height: "5rem" }} />
            </Card.Body>
            <Card.Footer className="text-center fixed-bottom bg-light border-top py-3">
              <Stack
                direction="horizontal"
                gap={3}
                className="justify-content-center"
              >
                <Button
                  variant="secondary"
                  disabled={currentTab === 0}
                  onClick={() => setCurrentTab((prev) => prev - 1)}
                >
                  <FontAwesomeIcon icon={faCaretLeft} className="me-2" />
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  disabled={currentTab >= modules.length}
                  onClick={() => setCurrentTab((prev) => prev + 1)}
                >
                  Siguiente
                  <FontAwesomeIcon icon={faCaretRight} className="ms-2" />
                </Button>
                <div className="vr" />
                <Button
                  variant="primary"
                  onClick={handleSaveButton}
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {isSaving ? "Guardando..." : "Guardar y continuar editando"}
                </Button>
                <Button type="submit" variant="success">
                  <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
                  Completar auditoría
                </Button>
              </Stack>
            </Card.Footer>
          </Card>
        </Form>
      </Container>
    </div>
  );
};

export default FillAudit;
